import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CustomMessageService } from 'src/app/services/utils/message.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { ErrorResponse, ListResponse } from 'src/app/interfaces/responses.interface';
import { map, of, Subscription } from 'rxjs';
import { isValid } from 'src/app/utils/code-utils';
import { Button } from 'primeng/button';
import { LinksService } from 'src/app/services/links.service';
import { Link } from 'src/app/interfaces/links.interface';

/**
 * Componente principal del menú lateral de la aplicación.
 *
 * Flujo de código: Se encarga de renderizar la estructura del menú obtenida del backend,
 * permitiendo navegación a través de routerLink y ofreciendo un filtro de búsqueda interno.
 *
 * Propósito: Proveer una barra lateral de navegación para los usuarios, adaptada dinámicamente.
 */
@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule, FormsModule, InputTextModule],
  styles: [
    `
      .input-menu-search {
        cursor: default;
        width: -webkit-fill-available;
        // background-color: var(--cnb-blue);
        background-color: transparent;
        // color: var(--primary-contrast-color);
        border-radius: 0px !important;
        border: 0px;
        border-bottom: 1px solid var(--primary-color);

        &:focus::placeholder {
          color: var(--primary-color);
        }

        &:enabled:focus {
          border: 0px;
          box-shadow: none;
          border-bottom: 2px solid var(--primary-color);
        }
      }
    `
  ],
  template: `
    <ul class="layout-menu">
      <input
        id="search-input"
        pInputText
        type="text"
        class="p-inputtext input-menu-search mt-2"
        placeholder="Buscar... (Ctrl + k)"
        autocomplete="auto"
        [(ngModel)]="searchQuery"
        (input)="onSearch($event)"
      />
      @for (item of filteredModel; track $index; let i = $index) {
        <li
          id="menu-item-{{ i }}"
          app-menuitem
          *ngIf="!item.separator"
          [item]="item"
          [index]="i"
          [root]="true"
        ></li>
        @if (item.separator) {
          <li class="menu-separator"></li>
        }
      }
    </ul>
  `
})
export class AppMenu implements OnInit {
  private destroyRef = inject(DestroyRef);
  model: MenuItem[] = [];
  filteredModel: any[] = [];
  searchQuery: string = '';

  private router: Router = inject(Router);
  private messageService: CustomMessageService = inject(CustomMessageService);
  private linksService: LinksService = inject(LinksService);
  private menuRequest?: Subscription;

  /**
   * Método inicializador del componente.
   *
   * Flujo de código: Carga el menú dinámico y agrega un event listener para atajar el atajo de
   * teclado 'Ctrl + k' para la búsqueda en el menú.
   *
   * Propósito: Configurar el estado inicial del componente una vez que es montado en el DOM.
   *
   * @returns {void}
   */
  ngOnInit() {
    this.linksService.menuChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cargarMenu());
    this.cargarMenu();

    document.addEventListener('keydown', (event) => {
      if (event.ctrlKey && event.key === 'k') {
        // focus on the input
        event.preventDefault();
        document.getElementById('search-input')?.focus();
      }
    });
  }

  /**
   * Carga los elementos del menú desde el servicio de enlaces de manera dinámica.
   *
   * Flujo de código: Se suscribe al observable del árbol de enlaces, ordena los enlaces,
   * los transforma al formato de MenuItem, y finalmente actualiza el modelo y el modelo filtrado.
   *
   * Propósito: Obtener las rutas y jerarquías dinámicas del backend para renderizar el menú lateral.
   *
   * @returns {void}
   */
  private cargarMenu(): void {
    this.menuRequest?.unsubscribe();
    this.menuRequest = this.linksService.arbol().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: ListResponse<Link>) => {
        if (!isValid(response.code) || !Array.isArray(response.data)) return;
        const linksOrdenados = response.data.sort((a, b) => {
          if (a.orden !== undefined && b.orden !== undefined) {
            if (a.orden !== b.orden) {
              return a.orden - b.orden;
            }
            return (a.id || 0) - (b.id || 0);
          }
          if (a.orden !== undefined) return -1;
          if (b.orden !== undefined) return 1;
          return (a.id || 0) - (b.id || 0);
        });

        const menuItems = this.transformarEnlacesAMenu(linksOrdenados);
        
        this.model = [
          {
            label: '',
            items: menuItems
          }
        ];

        // Inicialmente muestra todo el menú y expande la ruta activa
        this.filterMenu();
      },
      error: (error) => {
        console.error('Error al cargar el menú:', error);
      }
    });
  }

  /**
   * Transforma un arreglo de enlaces en la estructura de menú requerida por PrimeNG.
   *
   * Flujo de código: Itera sobre cada enlace, verifica si tiene páginas visibles o sub-enlaces (hijos).
   * Si es un enlace directo a una página, crea el MenuItem. Si tiene sub-enlaces, realiza una
   * llamada recursiva para anidar la estructura correctamente.
   *
   * Propósito: Adaptar la estructura de datos del backend a la interfaz que necesita
   * el componente de menú lateral.
   *
   * @param {Link[]} links - Arreglo de enlaces a procesar.
   * @returns {MenuItem[]} - Arreglo de opciones de menú formateado para PrimeNG.
   */
  private transformarEnlacesAMenu(links: Link[]): MenuItem[] {
    const menuItems: MenuItem[] = [];

    links.forEach((link) => {
      const paginasVisibles = link.paginas?.filter((pagina) => pagina.visible === 'SI' && pagina.route) || [];
      const tieneHijos = link.hijos && link.hijos.length > 0;

      if (!tieneHijos && paginasVisibles.length === 0) {
        return;
      }

      if (!tieneHijos && paginasVisibles.length === 1) {
        const pagina = paginasVisibles[0];
        const menuItem: MenuItem = {
          label: pagina.nombre,
          icon: 'nav-icon fas fa-circle', // Agregamos un icono por defecto
          routerLink: [pagina.route!]
        };
        menuItems.push(menuItem);
        return;
      }

      const menuItem: MenuItem = {
        label: link.label,
        icon: 'nav-icon fas fa-folder' // Agregamos un icono por defecto para carpetas
      };

      if (paginasVisibles.length > 0) {
        menuItem.items = paginasVisibles.map((pagina) => ({
          label: pagina.nombre,
          icon: 'nav-icon fas fa-circle',
          routerLink: [pagina.route!]
        }));
      }

      if (tieneHijos) {
        const subItems = this.transformarEnlacesAMenu(link.hijos!);
        if (subItems.length > 0) {
          if (menuItem.items) {
            menuItem.items = [...menuItem.items, ...subItems];
          } else {
            menuItem.items = subItems;
          }
        }
      }

      if (menuItem.items && menuItem.items.length > 0) {
        menuItems.push(menuItem);
      }
    });

    return menuItems;
  }

  /**
   * Evento desencadenado al escribir en la barra de búsqueda del menú.
   *
   * Flujo de código: Llama al método de filtrado de menú.
   *
   * Propósito: Reaccionar a la entrada del usuario en el campo de texto de búsqueda.
   *
   * @param {any} event - Evento del input de búsqueda.
   * @returns {void}
   */
  onSearch(event: any): void {
    this.filterMenu();
  }

  /**
   * Filtra los elementos del menú basados en la consulta de búsqueda.
   *
   * Flujo de código: Normaliza la cadena de texto y compara recursivamente contra
   * las etiquetas de los MenuItem, clonando el árbol para reconstruir los que hacen match.
   *
   * Propósito: Ocultar los elementos que no coincidan con la búsqueda para facilitar
   * la navegación al usuario.
   *
   * @returns {void}
   */
  filterMenu(): void {
    const query = this.searchQuery.toLowerCase();
    if (query === '') {
      this.filteredModel = this.cloneModelAndResetExpansion(this.model);
      this.openActivePath(this.filteredModel, this.router);
    } else {
      // Se recorre cada elemento recursivamente
      this.filteredModel = this.model
        .map((item) => this.filterMenuItem(item, query))
        .filter((item) => item != null);
    }
  }

  /**
   * Clona el modelo de menú y resetea propiedades de expansión y búsqueda.
   *
   * Flujo de código: Recorre recursivamente los elementos removiendo 'expanded',
   * 'searchActive' y 'query'.
   *
   * Propósito: Proveer una copia limpia del estado del menú para re-renderizar.
   *
   * @param {MenuItem[]} model - El modelo del menú a clonar.
   * @returns {MenuItem[]} - Una copia fresca y limpia del modelo.
   */
  cloneModelAndResetExpansion(model: MenuItem[]): MenuItem[] {
    return model.map((item) => {
      const clonedItem: MenuItem = { ...item };
      delete clonedItem.expanded;
      delete clonedItem['searchActive'];
      delete clonedItem['query'];
      if (clonedItem.items) {
        clonedItem.items = this.cloneModelAndResetExpansion(clonedItem.items as MenuItem[]);
      }
      return clonedItem;
    });
  }

  /**
   * Abre la ruta activa en el menú lateral para que sea visible desde el inicio.
   *
   * Flujo de código: Verifica en cascada si un ítem padre tiene algún ítem hijo activo
   * comparando con la ruta actual en el Router. En caso afirmativo, se expande.
   *
   * Propósito: Mantener la consistencia visual y mostrar al usuario en qué sección se encuentra.
   *
   * @param {MenuItem[]} items - Los ítems a revisar.
   * @param {Router} router - Instancia del enrutador de Angular.
   * @returns {boolean} - True si hay alguna ruta activa, False si no la hay.
   */
  openActivePath(items: MenuItem[], router: Router): boolean {
    let parentShouldBeOpen = false;
    for (const item of items) {
      let itemIsDirectlyActive = false;
      if (item.routerLink) {
        const link = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
        const options = item.routerLinkActiveOptions || {
          paths: 'exact',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored'
        };
        if (router.isActive(link as string, options) || router.url.includes(link as string)) {
          itemIsDirectlyActive = true;
        }
      }

      let childIsActive = false;
      if (item.items && item.items.length > 0) {
        childIsActive = this.openActivePath(item.items as MenuItem[], router);
      }

      if (itemIsDirectlyActive || childIsActive) {
        item.expanded = true;
        parentShouldBeOpen = true;
      }
    }
    return parentShouldBeOpen;
  }

  /**
   * Normaliza el texto removiendo acentos, espacios y convirtiendo a minúsculas
   * para hacer comparaciones más flexibles en la búsqueda.
   *
   * Flujo de código: Usa lowerCase y Normalización NFD para remover diacríticas y espacios.
   *
   * Propósito: Procesar strings para búsqueda insensible de acentos y espacios.
   *
   * @param {string} text - El texto a normalizar.
   * @returns {string} - Texto normalizado sin acentos ni espacios.
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD') // Descompone caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .replace(/\s+/g, ''); // Remueve todos los espacios
  }

  /**
   * Verifica si la consulta hace match con el texto de manera flexible.
   * Los caracteres de la consulta deben aparecer en orden en el texto.
   * Solo retorna true si se encuentran TODOS los caracteres de la consulta.
   *
   * Flujo de código: Se itera el texto comparando cada uno de los caracteres del string
   * buscado hasta consumirlo por completo.
   *
   * Propósito: Permitir al usuario encontrar ítems escribiendo parte de las palabras o saltando caracteres.
   *
   * @param {string} text - Texto del ítem.
   * @param {string} query - Consulta de búsqueda.
   * @returns {boolean} - Resultado de la verificación flexible.
   */
  private flexibleMatch(text: string, query: string): boolean {
    const normalizedText = this.normalizeText(text);
    const normalizedQuery = this.normalizeText(query);

    if (normalizedQuery.length === 0) {
      return false;
    }

    let textIndex = 0;
    let queryIndex = 0;

    for (const caracter of normalizedText) {
      if (caracter === normalizedQuery[queryIndex]) {
        queryIndex++;
      }
      textIndex++;
      if (queryIndex === normalizedQuery.length) {
        break;
      }
    }

    // Solo es un match si encontramos TODOS los caracteres de la consulta
    const isMatch = queryIndex === normalizedQuery.length;

    return isMatch;
  }

  /**
   * Filtra recursivamente un ítem en base a una búsqueda.
   *
   * Flujo de código: Realiza match en el ítem actual; si es un submenú, busca en sus descendientes
   * y los agrupa, conservando los contenedores de los que cumplen la condición.
   *
   * Propósito: Discernir qué partes de la rama del menú deben incluirse al filtrar resultados de búsqueda.
   *
   * @param {any} item - Elemento de menú actual.
   * @param {string} query - Cadena de búsqueda ingresada.
   * @returns {any | null} - El propio ítem (y sus hijos) modificado o nulo si no hay match.
   */
  filterMenuItem(item: any, query: string): any {
    const labelMatch = item.label && this.flexibleMatch(item.label, query);

    if (item.items) {
      // Es un nodo con hijos (submenú)
      if (labelMatch) {
        // El label de este submenú coincide. Mostrar todos sus hijos.
        // Todos los hijos (y sus descendientes) deben obtener la 'query' para el resaltado y activarse.
        const allChildrenPrepared = item.items.map((child: any) =>
          this.enrichItemWithQueryAndActivate(child, query)
        );
        return { ...item, items: allChildrenPrepared, searchActive: true, query };
      } else {
        // El label de este submenú NO coincide. Filtrar los hijos.
        const filteredChildren = item.items
          .map((child: any) => this.filterMenuItem(child, query)) // Recursión normal
          .filter((child: any) => child != null);

        if (filteredChildren.length > 0) {
          // Si algún hijo (o descendiente) coincide, mostrar este submenú con los hijos filtrados.
          return { ...item, items: filteredChildren, searchActive: true, query };
        }
        // Ni este submenú ni ninguno de sus hijos/descendientes coinciden.
        return null;
      }
    } else {
      // Es un item hoja (sin hijos)
      if (labelMatch) {
        // El label de este item hoja coincide.
        return { ...item, query }; // Añadir query para resaltado
      }
      // El item hoja no coincide.
      return null;
    }
  }

  /**
   * Función auxiliar para propagar la query de búsqueda a los sub-ítems y activarlos visualmente.
   *
   * Flujo de código: Copia el ítem, inserta los marcadores de búsqueda activa y se repite
   * el proceso en todos sus posibles hijos.
   *
   * Propósito: Resaltar los menús enteros cuando el grupo padre coincide con la búsqueda.
   *
   * @param {any} originalItem - Ítem original del menú.
   * @param {string} query - Texto de búsqueda.
   * @returns {any} - Elemento de menú con las banderas de búsqueda activas.
   */
  enrichItemWithQueryAndActivate(originalItem: any, query: string): any {
    const itemWithQuery = { ...originalItem, query, searchActive: true };
    if (itemWithQuery.items) {
      itemWithQuery.items = itemWithQuery.items.map(
        (child: any) => this.enrichItemWithQueryAndActivate(child, query) // Recursivamente para los hijos
      );
    }
    return itemWithQuery;
  }
}
